import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'
import { ReactComponent as IconArrowRight } from 'images/arrow-right.svg';
import Page from 'modules/app/components/Page';
import Skeleton from 'components/shared/Skeleton';
import PageContainer from 'components/shared/PageContainer';
import { ReactComponent as IconSearch } from 'images/search.svg';
import { ReactComponent as IconClear } from 'images/icons/search/clear.svg';
import styles from 'modules/playlists/components/Playlist/Playlist.module.scss';
import ExpertView from 'components/shared/ExpertView';
import clsx from 'clsx';
import NoContent from 'components/shared/NoContent/NoContent';
import Input from 'components/shared/Input';
import useGetExperts from 'sdk/hooks/profile/useGetExperts';
import useUserFollowingList from 'sdk/hooks/follow/useUserFollowingList';
import useUser from 'sdk/hooks/user/useUser';

function ExpertsSkeleton() {
  const navigate = useNavigate()
  return (
    <PageContainer className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.h} onClick={() => navigate(-1)}>
          <div className={styles.title}>
            <IconArrowRight />
            New Experts
          </div>
        </div>
      </div>
      <div className={styles.playlistPosts}>
        {Array.from(Array(12).keys()).map((item: number) => (
          <div className={styles.playlistItem} key={item}>
            <Skeleton className={styles.playlistItemSkeleton} />
          </div>
        ))}
      </div>
    </PageContainer>
  )
}

function Experts() {
  const [search, setSearch] = useState('')
  const [openSearch, setOpenSearch] = useState(false)
  const { data, isSuccess, isLoading } = useGetExperts()
  const [experts, setExperts] = useState(data)
  const [loaded, setLoaded] = useState(false);
  const [activePostIndex, setActivePostIndex] = useState(-1)
  const userData = useUser()
  const { data: userFollowingData } = useUserFollowingList(userData.data?.user?.id ?? '')
  const navigate = useNavigate()

  const handleSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSearch((e.target as HTMLInputElement).value)
  }

  useEffect(() => {
    if (isSuccess) {
      setLoaded(true)
      setExperts(data)
    }
  }, [isSuccess])

  if (isLoading && !loaded) {
    return (<ExpertsSkeleton />)
  }

  return (
    <PageContainer className={styles.container}>
      <div className={styles.headerRow}>
        <div
          className={clsx(
            styles.h,
            openSearch && styles.h_search,
          )}
          onClick={() => navigate(-1)}
        >
          <h1 className={styles.title}>
            <IconArrowRight />
            New Experts
          </h1>
        </div>
        {openSearch && (
          <Input
            icon={<IconSearch className={styles.prefix} />}
            suffix={<IconClear className={styles.suffix} onClick={() => setOpenSearch(false)} />}
            className={styles.searchInput}
            value={search}
            onChange={handleSearchChange}
            placeholder="Search on Beautify"
          />
        )}
      </div>
      {!userFollowingData?.pages.some((page) => !!page.length) && (
        <div className={styles.newUserBanner}>
          Follow an expert to stay up to date with the latest on stocks, crypto, ETFs and more
        </div>
      )}
      {experts?.length ? (
        <div className={clsx(
          styles.playlistPosts,
          isLoading && styles.playlistPosts_loading,
        )}
        >
          {experts?.map((expert, indx) => (
            <div
              className={styles.playlistItem}
              key={expert?.expert_id}
              onMouseEnter={() => setActivePostIndex(indx)}
              onMouseLeave={() => setActivePostIndex(-1)}
            >
              <ExpertView
                data={expert}
                className={styles.gridPostItem}
                isNew
                isActive={indx === activePostIndex}
              />
            </div>
          ))}
        </div>
      ) : null}
      {!experts?.length && (
        <div className={isLoading ? styles.playlistPosts_loading : ''}>
          <NoContent
            message="No data satisfying the filters"
          />
        </div>
      )}
    </PageContainer>
  )
}

function ExpertsPage() {
  return (
    <Page activeLink="/">
      <Experts />
    </Page>
  )
}

export default ExpertsPage
